import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Select,
  Loader,
  Center,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertCircle } from 'react-feather';
import { jobTitlesApi } from '../../../services/api/jobTitles';
import { usersApi, type InternalUser } from '../../../services/api/users';
import type { CohortWithDetails, CohortJobTitleAssignment, JobTitleAssignmentInput } from '../../../types';
import classes from './EditModals.module.css';

interface EditJobAssignmentsModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  onUpdate: (cohort: CohortWithDetails) => void;
}

export function EditJobAssignmentsModal({
  opened,
  onClose,
  cohort,
  onUpdate,
}: EditJobAssignmentsModalProps) {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<InternalUser[]>([]);
  const [jobAssignments, setJobAssignments] = useState<CohortJobTitleAssignment[]>([]);
  const [assignments, setAssignments] = useState<Record<string, string | null>>({});

  // Fetch data when modal opens
  useEffect(() => {
    if (opened) {
      const fetchData = async () => {
        setLoading(true);
        try {
          // Fetch users and job assignments in parallel
          const [usersData, assignmentsData] = await Promise.all([
            usersApi.listInternal(),
            jobTitlesApi.getCohortAssignments(cohort.id),
          ]);

          setUsers(usersData);
          setJobAssignments(assignmentsData);

          // Initialize assignments state from fetched data
          const initial: Record<string, string | null> = {};
          assignmentsData.forEach(assignment => {
            initial[assignment.job_title_id] = assignment.user_id;
          });
          setAssignments(initial);
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to load data',
            color: 'red',
          });
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [opened, cohort.id]);

  const handleAssignmentChange = (jobTitleId: string, userId: string | null) => {
    setAssignments(prev => ({ ...prev, [jobTitleId]: userId }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Convert to API format
      const data: JobTitleAssignmentInput[] = Object.entries(assignments).map(([job_title_id, user_id]) => ({
        job_title_id,
        user_id,
      }));

      await jobTitlesApi.updateCohortAssignments(cohort.id, data);

      // Re-fetch cohort to get updated data
      // Note: The API doesn't return the full cohort, so we pass the existing one
      // In a real scenario, you might want to refetch the cohort
      onUpdate(cohort);
      onClose();

      notifications.show({
        title: 'Success',
        message: 'Job title assignments updated',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update job assignments',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInitialize = async () => {
    try {
      setLoading(true);
      const newAssignments = await jobTitlesApi.initializeAssignments(cohort.id);
      setJobAssignments(newAssignments);

      // Update assignments state
      const initial: Record<string, string | null> = {};
      newAssignments.forEach(assignment => {
        initial[assignment.job_title_id] = assignment.user_id;
      });
      setAssignments(initial);

      notifications.show({
        title: 'Success',
        message: 'Job title assignments initialized',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to initialize assignments',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  // Format users for Select component
  const userOptions = users.map(user => ({
    value: user.id,
    label: `${user.name} (${user.email})`,
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Assign Job Titles"
      size="lg"
    >
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : jobAssignments.length === 0 ? (
        <Stack gap="md">
          <Alert icon={<AlertCircle size={16} />} color="blue">
            <Text size="sm">
              No job title assignments exist for this cohort yet. Initialize assignments to assign
              team members to job titles for task management.
            </Text>
          </Alert>
          <Group justify="flex-end" className="modal-footer">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleInitialize}>
              Initialize Assignments
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Assign team members to job titles. These assignments determine who receives task
            notifications and who is responsible for specific types of work.
          </Text>

          <Stack gap="sm">
            {jobAssignments.map(assignment => (
              <Group key={assignment.job_title_id} grow>
                <div style={{ flex: '0 0 180px' }}>
                  <Text size="sm" fw={500}>
                    {assignment.job_title_name}
                  </Text>
                  {assignment.job_title_description && (
                    <Text size="xs" c="dimmed">
                      {assignment.job_title_description}
                    </Text>
                  )}
                </div>
                <Select
                  placeholder="Select team member"
                  data={userOptions}
                  value={assignments[assignment.job_title_id]}
                  onChange={(value) => handleAssignmentChange(assignment.job_title_id, value)}
                  clearable
                  searchable
                  className={classes.roleSelect}
                />
              </Group>
            ))}
          </Stack>

          <Group justify="flex-end" className="modal-footer">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              Save Assignments
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export default EditJobAssignmentsModal;
